import { useState, useRef, useCallback, useEffect } from "react"
import {
  X,
  Upload,
  FileText,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Trash2,
  Plus,
} from "lucide-react"
import { db } from "../services/firebase"
import { doc, setDoc, serverTimestamp } from "firebase/firestore"

// ── Design tokens (mirror Leads.jsx) ─────────────────────────────────────────
const FF   = "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const MONO = "'IBM Plex Mono', monospace"

const T = {
  border:       "0.5px solid #e5e7eb",
  radius:       8,
  radiusCard:   10,
  bg:           "#ffffff",
  bgMuted:      "#fafafa",
  bgSubtle:     "#f7f7f7",
  text:         "#111827",
  textMuted:    "#374151",
  textTertiary: "#6b7280",
  shadow:       "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
  shadowLg:     "0 12px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.05)",
}

// ── API helpers ───────────────────────────────────────────────────────────────
const parseResume = async (resumeFile) => {
  const formData = new FormData()
  formData.append("resumeFile", resumeFile)
  const response = await fetch(
    "https://hire100x-e1dd4.uc.r.appspot.com/api/parse-resume",
    { method: "POST", body: formData }
  )
  if (!response.ok) throw new Error(`Parse failed (${response.status})`)
  const json = await response.json()
  if (!json.success) throw new Error(json.message || "Parse failed")
  return json.data
}

const saveUserToFirestore = async (parsedData) => {
  const userId = btoa(parsedData.email)
  const userRef = doc(db, "users", userId)
  await setDoc(
    userRef,
    { ...parsedData, accountType: "candidate", createdAt: serverTimestamp(), source: "external" },
    { merge: true }
  )
  return userId
}

const addLeadToJob = async (jobId, userId) => {
  const response = await fetch(
    `https://hire100x-e1dd4.uc.r.appspot.com/jobListings/${jobId}/add-lead`,
    {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ userId }),
    }
  )
  if (!response.ok) throw new Error(`Add lead failed (${response.status})`)
  return response.json()
}

const formatBytes = (bytes) => {
  if (bytes < 1024)        return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// ── Ring + countdown (ported from AddResumeModal pattern) ─────────────────────
const COUNTDOWN_START = 30
const RING_R = 44
const RING_C = 2 * Math.PI * RING_R   // ≈ 276.46

const CountdownRing = ({ secondsLeft, overdue }) => {
  const progress    = Math.max(0, secondsLeft / COUNTDOWN_START)
  const dashOffset  = RING_C - progress * RING_C
  const strokeColor = overdue ? "#f59e0b" : "#111827"

  return (
    <div style={{ position: "relative", width: 112, height: 112, flexShrink: 0 }}>
      {/* SVG ring */}
      <svg
        width="112" height="112" viewBox="0 0 112 112"
        style={{ transform: "rotate(-90deg)" }}
      >
        {/* Track */}
        <circle cx="56" cy="56" r={RING_R}
          fill="none" stroke="#f3f4f6" strokeWidth="3.5" />
        {/* Arc */}
        <circle cx="56" cy="56" r={RING_R}
          fill="none"
          stroke={strokeColor}
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeDasharray={RING_C}
          strokeDashoffset={dashOffset}
          style={{ transition: "stroke-dashoffset 0.9s linear, stroke 0.35s ease" }}
        />
      </svg>

      {/* Centre: number ticks with key= remount trick from AddResumeModal */}
      <div style={{
        position:       "absolute",
        inset:          0,
        display:        "flex",
        flexDirection:  "column",
        alignItems:     "center",
        justifyContent: "center",
        gap:            2,
      }}>
        {overdue ? (
          /* "…" when countdown expired */
          <span style={{
            fontSize:   22,
            fontWeight: 500,
            color:      "#f59e0b",
            fontFamily: MONO,
            lineHeight: 1,
          }}>
            …
          </span>
        ) : (
          <>
            {/* key={secondsLeft} forces remount each tick → animation replays */}
            <span
              key={secondsLeft}
              style={{
                fontSize:      28,
                fontWeight:    500,
                lineHeight:    1,
                letterSpacing: "-0.03em",
                color:         T.text,
                fontFamily:    MONO,
                animation:     "alm-num 0.18s ease forwards",
              }}
            >
              {String(secondsLeft).padStart(2, "0")}
            </span>
            <span style={{
              fontSize:      9,
              fontWeight:    500,
              letterSpacing: "0.1em",
              color:         T.textTertiary,
              fontFamily:    MONO,
            }}>
              SEC
            </span>
          </>
        )}
      </div>
    </div>
  )
}

// ── ActionBtn ─────────────────────────────────────────────────────────────────
const ActionBtn = ({ onClick, children, variant = "ghost", disabled, style: extra, ...rest }) => {
  const v = {
    ghost:   { bg: T.bgSubtle,  color: T.textMuted,  border: T.border                    },
    dark:    { bg: "#111827",   color: "#fff",        border: "none"                      },
    outline: { bg: T.bg,        color: T.textMuted,  border: T.border                    },
  }[variant] || { bg: T.bgSubtle, color: T.textMuted, border: T.border }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display:       "inline-flex",
        alignItems:    "center",
        gap:           6,
        height:        34,
        padding:       "0 14px",
        borderRadius:  T.radius,
        border:        v.border,
        background:    v.bg,
        color:         v.color,
        fontSize:      12,
        fontWeight:    600,
        fontFamily:    FF,
        letterSpacing: "-0.01em",
        cursor:        disabled ? "not-allowed" : "pointer",
        whiteSpace:    "nowrap",
        opacity:       disabled ? 0.38 : 1,
        transition:    "opacity 0.12s, background 0.12s",
        outline:       "none",
        ...extra,
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.opacity = "0.78" }}
      onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.opacity = "1"    }}
      {...rest}
    >
      {children}
    </button>
  )
}

// ── Status indicator colours ──────────────────────────────────────────────────
const STATUS = {
  idle:     { color: "#d1d5db" },
  parsing:  { color: "#3b82f6" },
  saving:   { color: "#8b5cf6" },
  applying: { color: "#6366f1" },
  done:     { color: "#10b981" },
  error:    { color: "#ef4444" },
}

// ── File row ──────────────────────────────────────────────────────────────────
const FileRow = ({ file, status, error, onRemove }) => {
  const cfg      = STATUS[status] || STATUS.idle
  const spinning = ["parsing", "saving", "applying"].includes(status)

  return (
    <div style={{
      display:      "flex",
      alignItems:   "center",
      gap:          10,
      padding:      "9px 12px",
      borderRadius: T.radius,
      border:       T.border,
      background:   status === "done"  ? "#f0fdf4"
                  : status === "error" ? "#fef2f2"
                  : T.bg,
      transition:   "background 0.2s",
    }}>
      {/* Icon */}
      <div style={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
        {spinning ? (
          <Loader2 size={13} color={cfg.color}
            style={{ animation: "alm-spin 0.8s linear infinite" }} />
        ) : status === "done" ? (
          <CheckCircle2 size={13} color="#10b981" />
        ) : status === "error" ? (
          <AlertCircle size={13} color="#ef4444" />
        ) : (
          <FileText size={13} color="#d1d5db" />
        )}
      </div>

      {/* Name */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          margin: 0, fontSize: 13, fontWeight: 600, color: T.text,
          fontFamily: FF, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {file.name}
        </p>
        {error ? (
          <p style={{ margin: "2px 0 0", fontSize: 11, color: "#ef4444", fontFamily: FF }}>
            {error}
          </p>
        ) : (
          <p style={{ margin: "2px 0 0", fontSize: 11, color: T.textTertiary, fontFamily: MONO }}>
            {formatBytes(file.size)}
          </p>
        )}
      </div>

      {/* Remove (idle only) */}
      {status === "idle" && onRemove && (
        <button
          onClick={() => onRemove(file.name)}
          style={{
            flexShrink: 0, display: "flex", alignItems: "center",
            background: "none", border: "none", cursor: "pointer",
            color: "#d1d5db", padding: 0, transition: "color 0.12s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = "#ef4444" }}
          onMouseLeave={(e) => { e.currentTarget.style.color = "#d1d5db" }}
        >
          <Trash2 size={13} />
        </button>
      )}
    </div>
  )
}

// ── Processing view ───────────────────────────────────────────────────────────
const ProcessingPanel = ({ files, fileStatuses, fileErrors, secondsLeft, overdue }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
    {/* Ring + label */}
    <div style={{
      display:        "flex",
      flexDirection:  "column",
      alignItems:     "center",
      gap:            14,
      padding:        "28px 20px 24px",
      borderRadius:   T.radiusCard,
      border:         overdue ? "0.5px solid #fde68a" : T.border,
      background:     overdue ? "#fffbeb" : T.bg,
      transition:     "background 0.4s, border-color 0.4s",
    }}>
      <CountdownRing secondsLeft={secondsLeft} overdue={overdue} />
      <div style={{ textAlign: "center" }}>
        <p style={{
          margin: 0, fontSize: 14, fontWeight: 600,
          color: overdue ? "#92400e" : T.text, fontFamily: FF,
        }}>
          {overdue ? "Almost done, hang on!" : "Processing resumes…"}
        </p>
        <p style={{
          margin: "4px 0 0", fontSize: 12, fontWeight: 500,
          color: overdue ? "#b45309" : T.textTertiary, fontFamily: FF,
        }}>
          {overdue
            ? "Taking a little longer than expected"
            : "Parsing, saving and adding leads"}
        </p>
      </div>
    </div>

    {/* Per-file list */}
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <p style={{
        margin: 0, fontSize: 11, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: "0.07em",
        color: T.textTertiary, fontFamily: MONO, paddingBottom: 2,
      }}>
        {files.length} resume{files.length !== 1 ? "s" : ""}
      </p>
      {files.map((file) => (
        <FileRow
          key={file.name}
          file={file}
          status={fileStatuses[file.name] || "idle"}
          error={fileErrors[file.name]}
        />
      ))}
    </div>
  </div>
)

// ── Main modal ────────────────────────────────────────────────────────────────
const AddLeadModal = ({
  jobId = "JOB_PLACEHOLDER",
  orgId = "ORGID_PLACEHOLDER",
  onClose,
  onSuccess,
}) => {
  const [files, setFiles]               = useState([])
  const [fileStatuses, setFileStatuses] = useState({})
  const [fileErrors, setFileErrors]     = useState({})
  const [isDragging, setIsDragging]     = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [successCount, setSuccessCount] = useState(0)
  const [secondsLeft, setSecondsLeft]   = useState(COUNTDOWN_START)
  const [overdue, setOverdue]           = useState(false)
  const inputRef  = useRef(null)
  const timerRef  = useRef(null)

  const allFinished =
    files.length > 0 &&
    files.every((f) => ["done", "error"].includes(fileStatuses[f.name]))

  const errorCount   = files.filter((f) => fileStatuses[f.name] === "error").length
  const pendingCount = files.filter((f) => fileStatuses[f.name] === "idle").length

  // Timer
  const startTimer = useCallback(() => {
    setSecondsLeft(COUNTDOWN_START)
    setOverdue(false)
    timerRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) { setOverdue(true); return 0 }
        return prev - 1
      })
    }, 1000)
  }, [])

  const stopTimer = useCallback(() => {
    clearInterval(timerRef.current)
    timerRef.current = null
  }, [])

  useEffect(() => () => stopTimer(), [stopTimer])

  // File helpers
  const setStatus = (name, s) => setFileStatuses((p) => ({ ...p, [name]: s }))
  const setError  = (name, m) => setFileErrors((p) => ({ ...p, [name]: m }))

  const addFiles = (incoming) => {
    const valid = Array.from(incoming).filter(
      (f) =>
        ["application/pdf", "application/msword",
         "application/vnd.openxmlformats-officedocument.wordprocessingml.document"]
          .includes(f.type) ||
        /\.(pdf|doc|docx)$/i.test(f.name)
    )
    if (!valid.length) return
    setFiles((prev) => {
      const existing = new Set(prev.map((f) => f.name))
      const fresh = valid.filter((f) => !existing.has(f.name))
      setFileStatuses((s) => { const n = { ...s }; fresh.forEach((f) => (n[f.name] = "idle")); return n })
      setFileErrors((e)   => { const n = { ...e }; fresh.forEach((f) => (n[f.name] = null));  return n })
      return [...prev, ...fresh]
    })
  }

  const removeFile = (name) => {
    setFiles((p) => p.filter((f) => f.name !== name))
    setFileStatuses((p) => { const n = { ...p }; delete n[name]; return n })
    setFileErrors((p)   => { const n = { ...p }; delete n[name]; return n })
  }

  const onDrop = useCallback((e) => {
    e.preventDefault(); setIsDragging(false); addFiles(e.dataTransfer.files)
  }, [])

  const processAll = async () => {
    const pending = files.filter((f) => !["done", "error"].includes(fileStatuses[f.name]))
    if (!pending.length || isProcessing) return

    setIsProcessing(true)
    startTimer()
    const alreadyDone = files.filter((f) => fileStatuses[f.name] === "done").length

    const results = await Promise.allSettled(
      pending.map(async (file) => {
        setStatus(file.name, "parsing")
        const parsed = await parseResume(file)
        setStatus(file.name, "saving")
        const userId = await saveUserToFirestore(parsed)
        setStatus(file.name, "applying")
        await addLeadToJob(jobId, userId)
        setStatus(file.name, "done")
      })
    )

    results.forEach((r, i) => {
      if (r.status === "rejected") {
        setStatus(pending[i].name, "error")
        setError(pending[i].name, r.reason?.message || "Something went wrong")
      }
    })

    stopTimer()
    setSuccessCount(alreadyDone + results.filter((r) => r.status === "fulfilled").length)
    setIsProcessing(false)
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
        @keyframes alm-spin     { to { transform: rotate(360deg); } }
        @keyframes alm-fade-in  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes alm-slide-up {
          from { opacity: 0; transform: translateY(12px) scale(0.988); }
          to   { opacity: 1; transform: translateY(0)    scale(1);     }
        }
        @keyframes alm-num {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        .alm-scroll::-webkit-scrollbar { display: none; }
        .alm-scroll { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* Backdrop */}
      <div
        onClick={!isProcessing ? onClose : undefined}
        style={{
          position: "fixed", inset: 0, zIndex: 40,
          background: "rgba(0,0,0,0.22)",
          backdropFilter: "blur(3px)",
          animation: "alm-fade-in 0.16s ease",
        }}
      />

      {/* Centering wrapper */}
      <div style={{
        position: "fixed", inset: 0, zIndex: 50,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
        pointerEvents: "none",
      }}>
        {/* Modal */}
        <div
          className="alm-scroll"
          style={{
            width: "100%", maxWidth: 460,
            maxHeight: "88vh",
            background: T.bg,
            borderRadius: T.radiusCard + 2,
            border: T.border,
            boxShadow: T.shadowLg,
            display: "flex", flexDirection: "column",
            pointerEvents: "all",
            overflow: "hidden",
            animation: "alm-slide-up 0.22s cubic-bezier(0.22,1,0.36,1)",
          }}
        >
          {/* ── Header ── */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "15px 20px",
            borderBottom: T.border,
            background: T.bg,
            flexShrink: 0,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {/* Dot accent — mirrors Leads top bar */}
              <div style={{
                width: 7, height: 7, borderRadius: "50%",
                background: "#111827", flexShrink: 0,
              }} />
              <span style={{
                fontSize: 11, fontWeight: 700,
                textTransform: "uppercase", letterSpacing: "0.08em",
                color: T.textTertiary, fontFamily: MONO,
              }}>
                Add Leads
              </span>
              <span style={{
                fontSize: 11, color: T.textTertiary, fontFamily: FF, fontWeight: 500,
              }}>
                Upload resumes — parsed and added automatically
              </span>
            </div>

            {!isProcessing && (
              <button
                onClick={onClose}
                style={{
                  width: 26, height: 26, display: "flex",
                  alignItems: "center", justifyContent: "center",
                  background: "none", border: T.border,
                  borderRadius: T.radius - 2, cursor: "pointer",
                  color: T.textTertiary, transition: "all 0.12s", outline: "none",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background   = "#111827"
                  e.currentTarget.style.color        = "#fff"
                  e.currentTarget.style.borderColor  = "#111827"
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background   = "none"
                  e.currentTarget.style.color        = T.textTertiary
                  e.currentTarget.style.borderColor  = "#e5e7eb"
                }}
              >
                <X size={12} strokeWidth={2.2} />
              </button>
            )}
          </div>

          {/* ── Body ── */}
          <div
            className="alm-scroll"
            style={{
              flex: 1, overflowY: "auto",
              padding: 20,
              display: "flex", flexDirection: "column", gap: 14,
              background: T.bgMuted,
            }}
          >
            {/* ── Processing view ── */}
            {isProcessing && (
              <ProcessingPanel
                files={files}
                fileStatuses={fileStatuses}
                fileErrors={fileErrors}
                secondsLeft={secondsLeft}
                overdue={overdue}
              />
            )}

            {/* ── Idle: dropzone + queue ── */}
            {!isProcessing && !allFinished && (
              <>
                {/* Drop zone */}
                <div
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={onDrop}
                  onClick={() => inputRef.current?.click()}
                  style={{
                    display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center",
                    gap: 10, padding: "30px 20px",
                    borderRadius: T.radiusCard,
                    border: `1.5px dashed ${isDragging ? "#111827" : "#d1d5db"}`,
                    background: isDragging ? "#f9fafb" : T.bg,
                    cursor: "pointer", transition: "all 0.14s",
                  }}
                >
                  <input
                    ref={inputRef} type="file" multiple accept=".pdf,.doc,.docx"
                    style={{ display: "none" }}
                    onChange={(e) => { addFiles(e.target.files); e.target.value = "" }}
                  />
                  <div style={{
                    width: 38, height: 38, borderRadius: T.radius,
                    background: T.bgSubtle, border: T.border,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Upload size={16} color="#6b7280" strokeWidth={2} />
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <p style={{
                      margin: 0, fontSize: 13, fontWeight: 600,
                      color: T.text, fontFamily: FF,
                    }}>
                      Drop resumes here or{" "}
                      <span style={{ textDecoration: "underline", textUnderlineOffset: 3 }}>
                        browse
                      </span>
                    </p>
                    <p style={{
                      margin: "4px 0 0", fontSize: 12, fontWeight: 500,
                      color: T.textTertiary, fontFamily: FF,
                    }}>
                      PDF, DOC, DOCX · Multiple files supported
                    </p>
                  </div>
                </div>

                {/* Queue */}
                {files.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                    <p style={{
                      margin: 0, fontSize: 11, fontWeight: 700,
                      textTransform: "uppercase", letterSpacing: "0.07em",
                      color: T.textTertiary, fontFamily: MONO, paddingBottom: 2,
                    }}>
                      {files.length} file{files.length !== 1 ? "s" : ""} queued
                    </p>
                    {files.map((file) => (
                      <FileRow
                        key={file.name}
                        file={file}
                        status={fileStatuses[file.name] || "idle"}
                        error={fileErrors[file.name]}
                        onRemove={removeFile}
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            {/* ── Success / partial-failure banner ── */}
            {allFinished && (
              <>
                <div style={{
                  display: "flex", alignItems: "flex-start", gap: 12,
                  padding: "14px 16px",
                  borderRadius: T.radiusCard,
                  border: `0.5px solid ${errorCount > 0 ? "#fde68a" : "#a7f3d0"}`,
                  background: errorCount > 0 ? "#fffbeb" : "#f0fdf4",
                  animation: "alm-fade-in 0.2s ease",
                }}>
                  <CheckCircle2
                    size={16}
                    color={errorCount > 0 ? "#d97706" : "#10b981"}
                    style={{ flexShrink: 0, marginTop: 1 }}
                  />
                  <div>
                    <p style={{
                      margin: 0, fontSize: 13, fontWeight: 700,
                      color: errorCount > 0 ? "#92400e" : "#065f46", fontFamily: FF,
                    }}>
                      {successCount} lead{successCount !== 1 ? "s" : ""} added successfully
                      {errorCount > 0 && ` · ${errorCount} failed`}
                    </p>
                    <p style={{
                      margin: "3px 0 0", fontSize: 12, fontWeight: 500,
                      color: errorCount > 0 ? "#b45309" : "#059669", fontFamily: FF,
                    }}>
                      New leads will appear in the New Leads section shortly.
                    </p>
                  </div>
                </div>

                {/* Read-only file list */}
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  {files.map((file) => (
                    <FileRow
                      key={file.name}
                      file={file}
                      status={fileStatuses[file.name] || "idle"}
                      error={fileErrors[file.name]}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* ── Footer ── */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "12px 20px",
            borderTop: T.border,
            background: T.bg,
            flexShrink: 0,
          }}>
            {allFinished ? (
              <>
                <span style={{
                  fontSize: 12, fontWeight: 500,
                  color: T.textTertiary, fontFamily: FF,
                }}>
                  Processing complete
                </span>
                <ActionBtn
                  variant="dark"
                  onClick={async () => { await onSuccess?.(); onClose() }}
                >
                  <CheckCircle2 size={12} strokeWidth={2.5} />
                  Done
                </ActionBtn>
              </>
            ) : (
              <>
                <ActionBtn
                  variant="outline"
                  onClick={!isProcessing ? onClose : undefined}
                  disabled={isProcessing}
                >
                  Cancel
                </ActionBtn>

                <ActionBtn
                  variant="dark"
                  onClick={processAll}
                  disabled={files.length === 0 || isProcessing || pendingCount === 0}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 size={12} style={{ animation: "alm-spin 0.8s linear infinite" }} />
                      Processing…
                    </>
                  ) : (
                    <>
                      <Plus size={12} strokeWidth={2.5} />
                      Process{" "}
                      {pendingCount > 0
                        ? `${pendingCount} Resume${pendingCount !== 1 ? "s" : ""}`
                        : "Resumes"}
                    </>
                  )}
                </ActionBtn>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default AddLeadModal